import type { TableSearchParameters } from '@/ui/Table/tableProperties';
import type { ColumnsAvailableProps } from '@/ui/Table/useColumnsFilter';
import useColumnsFilter from '@/ui/Table/useColumnsFilter';
import TableColumnStorage from '@/util/localStorage/TableColumnStorage';
import type { FileAiReviewFragmentFragment } from '@/graphql/generated/graphql';
import { protolusColumnsAvailable, protolusColumnsOrder } from '~/ui/Table/protulus/protolusColumns';

type Columns = keyof FileAiReviewFragmentFragment | 'onView';

const columnsOrder: Columns[] = [
  'id',
  'explanation',
  'status',
  'confidence_score',
  'model_driver',
  'processing_seconds',
  'error_response',
  'onView',
  'file_ai_prompt_id',
  'file_content_id',
  ...protolusColumnsOrder,
];

const storage = new TableColumnStorage<Columns[]>('fileAiReviews', [
  'id',
  'status',
  'file_ai_prompt_id',
  'file_content_id',
  'confidence_score',
  'explanation',
  'model_driver',
  'processing_seconds',
  'error_response',
  'onView',
  'creation_time',
  'last_update_time',
  'last_update_by',
  'created_by',
]);

const columnsAvailables: ColumnsAvailableProps<Columns> = {
  id: {
    title: 'ID',
    dataIndex: 'id',
    key: 'id',
    width: 10,
    sorter: true,
  },
  status: {
    title: 'Status',
    dataIndex: 'status',
    key: 'status',
    width: 10,
    sorter: true,
  },
  file_ai_prompt_id: {
    title: 'Prompt',
    dataIndex: 'file_ai_prompt_id',
    key: 'file_ai_prompt_id',
    width: 12,
    sorter: true,
  },
  file_content_id: {
    title: 'File content ID',
    dataIndex: 'file_content_id',
    key: 'file_content_id',
    width: 10,
    sorter: true,
  },
  confidence_score: {
    title: 'Confidence',
    dataIndex: 'confidence_score',
    key: 'confidence_score',
    width: 10,
    sorter: true,
  },
  explanation: {
    title: 'Explanation',
    dataIndex: 'explanation',
    key: 'explanation',
  },
  model_driver: {
    title: 'Model',
    dataIndex: 'model_driver',
    key: 'model_driver',
    width: 10,
    sorter: true,
  },
  processing_seconds: {
    title: 'Processing',
    dataIndex: 'processing_seconds',
    key: 'processing_seconds',
    width: 10,
    sorter: true,
  },
  error_response: {
    title: 'Error',
    dataIndex: 'error_response',
    key: 'error_response',
  },
  onView: {
    title: 'View',
    dataIndex: 'onView',
    key: 'onView',
  },
  ...protolusColumnsAvailable,
};

interface FileAiReviewsColumnsArgs {
  orderBy: TableSearchParameters['orderBy'];
}

const useFileAiReviewsColumns = ({ orderBy }: FileAiReviewsColumnsArgs) => {
  const columns = useColumnsFilter({
    orderBy,
    storage,
    columnsOrder,
    columnsAvailables,
  });

  return columns;
};

export default useFileAiReviewsColumns;
